/**
 * Auth Store - Zustand state management for authentication
 * Handles user session, JWT tokens, permission checks, and module entitlements.
 */

import { create } from 'zustand';
import { devtools, persist, createJSONStorage } from 'zustand/middleware';
import type {
  ModuleCap,
  ModuleAccessMap,
  Entitlements,
  LicensedModule,
  WorkflowAccessLevel,
  DataScope,
  ScopedResource,
} from '@upskill/shared';
import {
  levelForWorkflow,
  scopeForResource as resolveScopeForResource,
  WORKFLOW_LEVEL_RANK,
} from '@upskill/shared';
import { getCurrentUser } from '../services/api/authService';
import { queryClient } from '../lib/queryClient';

export type AccessLevel = 'READ' | 'FULL';
export type { ModuleCap, LicensedModule };

const ACCESS_LEVEL_RANK: Record<string, number> = { READ: 1, FULL: 2 };

const SUPPORT_WIDGET_STORAGE_KEY = 'support-widget-storage';

/**
 * Identity boundary: the support widget's compose draft is free text users
 * may paste PHI into, persisted (in supportWidgetStore) to sessionStorage so
 * it survives an accidental tab close. sessionStorage alone does NOT clear
 * it on logout-then-login in the same tab — it's scoped to the tab, not the
 * session — so both `clearAuth()` and `beginLogout()` explicitly evict the
 * key here, from both sessionStorage and localStorage (existing installs may
 * still have it in localStorage from before the store switched off it).
 */
function clearSupportWidgetStorage(): void {
  try {
    sessionStorage.removeItem(SUPPORT_WIDGET_STORAGE_KEY);
    localStorage.removeItem(SUPPORT_WIDGET_STORAGE_KEY);
  } catch {
    // Storage can throw in locked-down/private-browsing contexts — never let
    // cleanup block logout.
  }
}

// Impersonation session data — never persisted to localStorage
export interface ImpersonationData {
  token: string;
  sessionId: string;
  targetUserId: string;
  targetUserName: string;
  targetOrgName?: string;
  expiresAt: string; // ISO 8601
}

// User type (from API response)
export interface User {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  organizationId: string;
  organizationName?: string;
  roles?: string[];
  permissions?: string[];
  /** Detailed permissions with access levels (from /auth/me, license-capped) */
  permissionLevels?: Record<string, AccessLevel>;
  /** License-gated module entitlements (from /auth/me). Sparse map — absent key = NONE. */
  entitlements?: Entitlements;
  /** Convenience list of modules whose effective cap is READ (from /auth/me). */
  readonlyModules?: string[];
  status: 'active' | 'inactive';
  isSuperAdmin?: boolean;
  onboardingComplete?: boolean;
  accountType?: 'INDIVIDUAL' | 'ORGANIZATION';
  /**
   * Pre-signed URL for the user's profile picture, produced server-side.
   * Present in /auth/me responses when a profile picture has been uploaded.
   * The URL is relative (works directly as <img src> under the Vite proxy).
   * Refresh via `authStore.refreshUser()` after upload/delete to get a fresh URL.
   */
  profilePic?: string;
  /**
   * When true the user must change their password before continuing.
   * Set by an admin password reset; cleared by a successful change-password call.
   * The frontend enforces this via a redirect gate in ProtectedRoute.
   */
  mustChangePassword?: boolean;
  /** When false the user cannot initiate login (Entra activation gate). */
  loginActivated?: boolean;
  /** Per-user inactivity timeout in minutes, sourced from /auth/me. */
  sessionTimeoutMinutes?: number;
}

interface AuthState {
  user: User | null;
  accessToken: string | null;
  refreshToken: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  /**
   * True for the brief window between the user initiating a full-page-redirect
   * logout (Entra end-session) and the browser actually leaving the page.
   * Never persisted — a fresh load always starts false. Also reset on a
   * back/forward-cache restore of this page (see the `pageshow` listener below).
   * See `beginLogout()`.
   */
  isLoggingOut: boolean;
  /** Active impersonation session. Never persisted to localStorage. */
  impersonation: ImpersonationData | null;
  /** HIPAA inactivity timeout in minutes (from /auth/me sessionTimeoutMinutes). Default 15. */
  sessionTimeoutMinutes: number;
}

interface AuthActions {
  setAuth: (user: User, accessToken: string, refreshToken: string) => void;
  /**
   * Stores tokens and marks session authenticated without setting the user.
   * Call this immediately after receiving tokens from the Entra callback, then
   * call getCurrentUser() and setAuth() to populate the user.
   */
  setTokens: (accessToken: string, refreshToken: string) => void;
  setAccessToken: (accessToken: string) => void;
  setPermissionLevels: (levels: Record<string, AccessLevel>) => void;
  clearAuth: () => void;
  /**
   * Full session reset (same as `clearAuth()`) plus `isLoggingOut: true`, for use
   * right before a synchronous full-page redirect (e.g. Entra end-session).
   * `ProtectedRoute` renders nothing while `isLoggingOut` is true instead of its
   * usual `<Navigate to="/login">` — that internal client-side navigation is what
   * paints the login screen and causes a visible flash before the browser actually
   * commits the redirect away (flash-then-bounce-to-Entra). Auth state itself is
   * still fully cleared and persisted here (unlike a no-op-on-the-store approach),
   * so there's no window where a stray `set()` racing the redirect could
   * re-persist a stale authenticated session, and a back/forward-cache restore of
   * this page can never show authenticated content. `clearAuth()` remains the
   * action for every other logout path (inactivity timeout, token-refresh
   * failure) where an in-app redirect to /login is the desired behavior.
   */
  beginLogout: () => void;
  setLoading: (isLoading: boolean) => void;
  /** Set or clear the active impersonation session. */
  setImpersonation: (data: ImpersonationData | null) => void;
  /** Check if user has permission (optionally at a specific level, defaults to any level) */
  hasPermission: (permission: string, requiredLevel?: AccessLevel) => boolean;
  hasAnyPermission: (permissions: string[]) => boolean;
  hasAllPermissions: (permissions: string[]) => boolean;
  isSuperAdmin: () => boolean;
  /** Derive the user's access level (none/execute/manage) for a workflow code. */
  workflowLevel: (code: string) => WorkflowAccessLevel;
  /** True when the user's workflow level is at least `min` (default 'execute'). */
  hasWorkflowAccess: (code: string, min?: WorkflowAccessLevel) => boolean;
  /** Derive the user's data scope (own/all) for Team or Clients. */
  dataScope: (resource: ScopedResource) => DataScope;
  /**
   * Returns the license cap for a module.
   * Absent key in moduleAccess is treated as 'NONE'.
   */
  getModuleCap: (module: LicensedModule) => ModuleCap;
  /** True when the module cap is READ or FULL (i.e. any access). */
  hasModule: (module: LicensedModule) => boolean;
  /** True when the module cap is exactly READ (read-only locked). */
  isModuleReadonly: (module: LicensedModule) => boolean;
  /**
   * Re-fetches /auth/me and updates the store with fresh user data including
   * entitlements. Call this after a license change (T19/T20/UPS-30) or on
   * app focus to ensure entitlement state is current.
   */
  refreshUser: () => Promise<void>;
  /**
   * Returns the raw plan-level permission allowlist, or undefined for open-gate.
   * Undefined means no allowlist is configured — all RBAC-granted permissions
   * are plan-permitted. An empty array means deny-all (no plan-permitted perms).
   */
  getEnabledPermissions: () => string[] | undefined;
  /**
   * Returns true if the permission key is plan-permitted.
   *
   * Gate semantics:
   *   - `undefined` (open-gate): returns true for any key.
   *   - Non-empty array: returns true only when key is present in the array.
   *   - Empty array (deny-all): returns false for every key.
   *
   * Intended consumer: P9 role editor — grey/hide any permission key for which
   * this returns false.
   */
  isPermissionEnabledByPlan: (key: string) => boolean;
}

type AuthStore = AuthState & AuthActions;

const initialState: AuthState = {
  user: null,
  accessToken: null,
  refreshToken: null,
  isAuthenticated: false,
  isLoading: false,
  isLoggingOut: false,
  impersonation: null,
  sessionTimeoutMinutes: 15,
};

export const useAuthStore = create<AuthStore>()(
  devtools(
  persist(
    (set, get) => ({
      ...initialState,

      setAuth: (user, accessToken, refreshToken) => {
        // Identity boundary: drop any cached React Query data from a previous
        // session so one user's data never bleeds into the next (cross-tenant
        // leak). Same operation the impersonation flow already performs.
        queryClient.clear();
        set({
          user,
          accessToken,
          refreshToken,
          isAuthenticated: true,
          isLoading: false,
          // A fresh, successfully-authenticated session is starting — clear any
          // stale isLoggingOut left behind by an aborted/failed logout redirect
          // in this same document, so ProtectedRoute doesn't stay stuck blank.
          isLoggingOut: false,
          sessionTimeoutMinutes: user.sessionTimeoutMinutes ?? 15,
        });
      },

      setTokens: (accessToken, refreshToken) => {
        // Arm the axios interceptor so the next getCurrentUser() call is authenticated.
        // Does NOT set isAuthenticated — only setAuth should do that.
        set({ accessToken, refreshToken });
      },

      setAccessToken: (accessToken) => {
        set({ accessToken });
      },

      setPermissionLevels: (levels) => {
        const { user } = get();
        if (user) {
          set({ user: { ...user, permissionLevels: levels } });
        }
      },

      clearAuth: () => {
        set(initialState);
        // Identity boundary: wipe cached React Query data on logout so it cannot
        // be served to the next user who logs in within the same tab. Covers all
        // logout paths (Header, inactivity timeout, token-refresh failure).
        queryClient.clear();
        clearSupportWidgetStorage();
      },

      beginLogout: () => {
        set({ ...initialState, isLoggingOut: true });
        // Identity boundary — see the doc comment on `clearAuth()` above.
        queryClient.clear();
        clearSupportWidgetStorage();
      },

      setLoading: (isLoading) => {
        set({ isLoading });
      },

      setImpersonation: (data) => {
        set({ impersonation: data });
      },

      hasPermission: (permission, requiredLevel?) => {
        const { user } = get();
        if (!user) return false;

        // Check via detailed permission levels if available
        if (user.permissionLevels) {
          const userLevel = user.permissionLevels[permission];
          if (!userLevel) return false;
          if (!requiredLevel) return true; // Any level counts
          return ACCESS_LEVEL_RANK[userLevel] >= ACCESS_LEVEL_RANK[requiredLevel];
        }

        // Fallback to flat permissions array (backward compat)
        if (!user.permissions) return false;
        return user.permissions.includes(permission);
      },

      hasAnyPermission: (permissions) => {
        const { user } = get();
        if (!user) return false;

        if (user.permissionLevels) {
          return permissions.some((p) => p in user.permissionLevels!);
        }

        if (!user.permissions) return false;
        return permissions.some((permission) => user.permissions!.includes(permission));
      },

      hasAllPermissions: (permissions) => {
        const { user } = get();
        if (!user) return false;

        if (user.permissionLevels) {
          return permissions.every((p) => p in user.permissionLevels!);
        }

        if (!user.permissions) return false;
        return permissions.every((permission) => user.permissions!.includes(permission));
      },

      isSuperAdmin: () => {
        const { user } = get();
        return user?.isSuperAdmin === true;
      },

      workflowLevel: (code) => {
        const { user } = get();
        if (!user) return 'none';
        // Permission-driven (no super-admin shortcut): super admins are a platform
        // persona with no workflow grants, so Workflows stays hidden for them.
        const keys = user.permissionLevels
          ? Object.keys(user.permissionLevels)
          : user.permissions ?? [];
        return levelForWorkflow(keys, code);
      },

      hasWorkflowAccess: (code, min = 'execute') => {
        return WORKFLOW_LEVEL_RANK[get().workflowLevel(code)] >= WORKFLOW_LEVEL_RANK[min];
      },

      dataScope: (resource) => {
        const { user } = get();
        if (!user) return 'own';
        if (user.isSuperAdmin) return 'all';
        const keys = user.permissionLevels
          ? Object.keys(user.permissionLevels)
          : user.permissions ?? [];
        return resolveScopeForResource(keys, resource);
      },

      getModuleCap: (module) => {
        const { user } = get();
        const moduleAccess: ModuleAccessMap = user?.entitlements?.moduleAccess ?? {};
        return moduleAccess[module]?.cap ?? 'NONE';
      },

      hasModule: (module) => {
        return get().getModuleCap(module) !== 'NONE';
      },

      isModuleReadonly: (module) => {
        return get().getModuleCap(module) === 'READ';
      },

      getEnabledPermissions: () => {
        return get().user?.entitlements?.enabledPermissions;
      },

      isPermissionEnabledByPlan: (key) => {
        const enabledPermissions = get().getEnabledPermissions();
        // undefined = open-gate: all permissions are plan-permitted
        if (enabledPermissions === undefined) return true;
        // empty array = deny-all; non-empty = allowlist check
        return enabledPermissions.includes(key);
      },

      refreshUser: async () => {
        const { user, accessToken } = get();
        // Only refresh when an authenticated session exists
        if (!user || !accessToken) return;

        try {
          const freshUser = await getCurrentUser();
          set((state) => ({
            user: state.user ? { ...state.user, ...freshUser } : freshUser,
          }));
        } catch {
          // Silently ignore — stale entitlements are preferable to a crash.
          // Components that need guaranteed freshness can check the result
          // of their own data fetch instead.
        }
      },
    }),
    {
      name: 'upskill-auth',
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        user: state.user,
        accessToken: state.accessToken,
        refreshToken: state.refreshToken,
        isAuthenticated: state.isAuthenticated,
        // isLoggingOut is deliberately excluded — it's a transient UI-only flag
        // for the in-flight logout redirect, never something to rehydrate.
      }),
    },
  ),
  { name: 'AuthStore' }),
);

// If this page is resurrected from the browser's back/forward cache (e.g. the
// user hits Back after an Entra logout redirect), `isLoggingOut` would otherwise
// stay frozen `true` forever — the in-memory state is restored exactly as it was
// when the page was frozen, and nothing on a bfcache restore re-runs beginLogout()
// or navigates. That would permanently strand ProtectedRoute on its "render
// nothing" branch. `pageshow` with `event.persisted` is the standard signal for a
// bfcache restore; clearing the flag here lets ProtectedRoute's normal
// `!isAuthenticated` → redirect-to-/login path take back over.
if (typeof window !== 'undefined') {
  window.addEventListener('pageshow', (event) => {
    if (event.persisted && useAuthStore.getState().isLoggingOut) {
      useAuthStore.setState({ isLoggingOut: false });
    }
  });
}
